"use strict";

import { BANNER, BANNER_CONFIRM_BUTTON, CHECK_CHANGES_INTERVAL, STORAGENAME, DEFAULT_DATASET_NAME, REJECTED, CONTENT, COOKIE, BANNER_PREFERENCES } from "@/helpers/constants";

import { storage } from "@/services/storage";
import { cookie } from "@/services/cookies";
import { api } from "@/services/api";

import { injectCookiedockDOM } from "@/view/inject/dom";
import { injectCookiedockCSS } from "@/view/inject/style";

import { getCookiedockCuuid } from "@/helpers";
import { getElement, addDomEvent } from "@/services/dom";

import type { Content, Cookiedock } from "@/view/types";
import { createModel } from "./utils/model";

import "@/helpers/polyfill.js";

interface Approved {
  [id: string]: { id: number; approved: boolean };
}

(() => {
  if ("onbeforescriptexecute" in document) return; // Already natively supported

  let scriptWatcher = new MutationObserver((mutations: MutationRecord[]) => {
    for (let mutation of mutations) {
      for (let i = 0; i < mutation.addedNodes.length; i++) {
        const node = mutation.addedNodes[i];
        if (node instanceof HTMLScriptElement) {
          let syntheticEvent = new CustomEvent<Node>("beforescriptexecute", {
            detail: node,
            cancelable: true,
          });
          if (!document.dispatchEvent(syntheticEvent)) {
            node.remove();
          }
        }
      }
    }
  });
  scriptWatcher.observe(document, {
    childList: true,
    subtree: true,
  });
})();

export const cuuid = getCookiedockCuuid(DEFAULT_DATASET_NAME);

export async function init() {
  setTestCookies;

  setInterval(removeCookiesByName, CHECK_CHANGES_INTERVAL);

  const cookiesStoredLocal = cookie.get(COOKIE);
  if (cookiesStoredLocal) return;

  const cookies = await api.get<Cookiedock>(`api/v1/${cuuid}/consent`);
  if (!cookies) return;

  storage.set<Content>(STORAGENAME, cookies.content, CONTENT);

  injectCookiedockDOM(cookies);

  await addListeners(cookies);
}

(async () => await init())();

export async function addListeners({ content, cookie_purposes }: Cookiedock) {
  injectCookiedockCSS(content);

  addDomEvent<HTMLButtonElement>(`.${BANNER_CONFIRM_BUTTON}`, "click", () => {
    const checkboxes = getElement<HTMLInputElement>(`.${BANNER_PREFERENCES}`, document, true);

    let accepted: Approved = { unknown: { id: 5, approved: false } };
    let rejected: any = [];

    checkboxes.forEach((checkbox: HTMLInputElement, index: number) => {
      accepted[checkbox.name] = { id: index + 1, approved: checkbox.checked };

      if (checkbox.checked === false) {
        rejected.push(...cookie_purposes[index].detected_cookies.map(({ cookie_name }) => cookie_name));
      }
    });

    storage.set(STORAGENAME, rejected, REJECTED);
    cookie.set(COOKIE, true, { expires: new Date(3000, 0, 1), secure: true, sameSite: "strict" });

    api.post(`api/v1/${cuuid}/consent`, { body: { cookie_purposes: accepted } });

    removeCookiesByName();

    const element = getElement<HTMLDivElement>(`.${BANNER}`, document, false);

    if (!element) return;
    element.remove();
  });
}

function removeCookiesByName() {
  const cookieDetails = storage.get<string[]>(STORAGENAME, REJECTED);
  if (!cookieDetails) return;

  const currentCookies = document.cookie;
  cookieDetails.forEach((name) => {
    if (currentCookies.includes(`${name}=`)) {
      cookie.remove(name);
    }
  });
}

async function setTestCookies() {
  const { cookie_purposes } = await api.get<Cookiedock>(`api/v1/${cuuid}/consent`);

  cookie_purposes.forEach(({ detected_cookies }) =>
    detected_cookies.forEach((detected) => cookie.set(detected.cookie_name, JSON.stringify(detected.cookie), { expires: detected.expiry * 10000 })),
  );
  return "";
}

window.openCookiedock = () => {
  cookie.remove(COOKIE);
  init();
};

// Create a model for the 'disabled' attribute of form elements

// Later disable all form controls
// formEnabledModel.set(true);

// addedNodes.forEach((node) => {
//   // See above for script tag checks…

//   node.type = "javascript/blocked";

//   // Firefox has this additional event which prevents scripts from beeing executed
//   const beforeScriptExecuteListener = function (event) {
//     // Prevent only marked scripts from executing
//     if (node.getAttribute("type") === "javascript/blocked") event.preventDefault();
//     node.removeEventListener("beforescriptexecute", beforeScriptExecuteListener);
//   };
//   node.addEventListener("beforescriptexecute", beforeScriptExecuteListener);
// });
